import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import { AuthProvider } from './auth/AuthContext';
import RequireAuth from './components/RequireAuth';
import PortalLayout from './components/PortalLayout';

import LoginPage from './pages/public/LoginPage';
import RegisterPage from './pages/public/RegisterPage';
import RequestAccessPage from './pages/public/RequestAccessPage';
import RedeemInvitePage from './pages/public/RedeemInvitePage';
import ConfirmPage from './pages/public/ConfirmPage';
import ForgotPasswordPage from './pages/public/ForgotPasswordPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import KeysPage from './pages/keys/KeysPage';
import UsagePage from './pages/usage/UsagePage';
import DomainsPage from './pages/domains/DomainsPage';
import WebhooksPage from './pages/webhooks/WebhooksPage';
import SettingsPage from './pages/settings/SettingsPage';
import DocsPage from './pages/docs/DocsPage';
import LegalAcceptancePage from './pages/legal/LegalAcceptancePage';
import ApiLogsPage from './pages/logs/ApiLogsPage';
import BillingPage from './pages/billing/BillingPage';
import InvitePeersPage from './pages/invite/InvitePeersPage';
import AppsPage from './pages/apps/AppsPage';

const theme = createTheme({
  palette: {
    primary: { main: '#3B82F6' },
    background: { default: '#F9FAFB', paper: '#FFFFFF' },
    text: { primary: '#09090B', secondary: '#52525B' },
    divider: '#E4E4E7',
  },
  shape: { borderRadius: 6 },
  typography: {
    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
    h1: { fontWeight: 800, letterSpacing: '-0.02em' },
    h2: { fontWeight: 800, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.02em' },
    h6: { fontWeight: 700, letterSpacing: '-0.02em' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          '&:active': { boxShadow: 'none' },
        },
      },
      defaultProps: { disableElevation: true },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid #E4E4E7',
        },
      },
    },
    MuiCard: {
      styleOverrides: { root: { boxShadow: 'none', border: '1px solid #E4E4E7' } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E4E4E7' },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: { root: { boxShadow: 'none' } },
    },
  },
});

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/request-access" element={<RequestAccessPage />} />
            <Route path="/redeem" element={<RedeemInvitePage />} />
            <Route path="/confirm" element={<ConfirmPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            {/* Legal acceptance gate — inside RequireAuth but outside PortalLayout */}
            <Route path="/legal/accept" element={<RequireAuth><LegalAcceptancePage /></RequireAuth>} />

            {/* Authenticated portal */}
            <Route path="/" element={<RequireAuth><PortalLayout /></RequireAuth>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="keys" element={<KeysPage />} />
              <Route path="usage" element={<UsagePage />} />
              <Route path="domains" element={<DomainsPage />} />
              <Route path="webhooks" element={<WebhooksPage />} />
              <Route path="invite" element={<InvitePeersPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="docs" element={<DocsPage />} />
              <Route path="apps" element={<AppsPage />} />
              <Route path="logs" element={<ApiLogsPage />} />
              <Route path="billing" element={<BillingPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
